import { Alert, Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { formatQty, shortfallAgainstFill } from '../utils/bulkLotNdcStatus';
import { warningTextTone, warningTint } from '../utils/warningTone';

/**
 * POM-6121 — the picked lot cannot cover the fill, stated in the operator's own numbers.
 *
 * Tells, does not ask. This carried a confirmation checkbox until POM-6119: on a bulk screen that
 * meant a tick per row, which is the kind of prompt operators learn to clear without reading — and
 * it proved nothing, because it never reached the server. What it gated was the Apply button, not
 * the payload. The claim is visible either way: it lands in the unverified bucket and an inventory
 * user still has to reconcile it against a receiving record.
 *
 * POM-6536 — the lot already has the rest of the batch taken off (withBatchShare), so the
 * quantity is what this row is left with, and Apply is the confirmation of the claim.
 *
 * A hand-recorded lot still asks, and deliberately: that asserts stock inventory has never seen at
 * all, which is a larger claim than a quantity being short.
 *
 * Amber, not an error: nothing is wrong. What is recorded is a claim against stock inventory has
 * no receipt for yet.
 *
 * Its own file because ChangeNdcLotDialog is at the 450-line cap — the same split its sibling
 * panels already use.
 */
const ChangeNdcLotShortfallNotice = ({ lot, fillQuantity, rxNumber, keepsLabel }) => {
  const deficit = SYSTEM_DEFICIT.toLowerCase();
  const shortfall = formatQty(shortfallAgainstFill({ lot, fillQuantity }));
  const isUsedUp = Number(lot?.remainingQty ?? 0) <= 0;

  return (
    <Alert
      severity='warning'
      icon={false}
      sx={{
        mt: 2,
        bgcolor: (theme) => warningTint(theme, 0.12),
        color: warningTextTone,
        border: '1px solid',
        borderColor: (theme) => warningTint(theme, 0.4),
      }}
    >
      <Typography variant='body2' sx={{ color: warningTextTone }}>
        {isUsedUp
          ? `Other rows in this batch use all ${formatQty(lot?.stockQty)} of lot ${
              lot?.lotNumber ?? ''
            }, so `
          : `Rx ${rxNumber ?? ''} needs ${formatQty(fillQuantity)}, so `}
        <Box component='span' fontWeight={700}>
          {isUsedUp
            ? `all ${formatQty(fillQuantity)} will be recorded as ${deficit}`
            : `${shortfall} will be recorded as ${deficit}`}
        </Box>
        {`. Apply to confirm.${keepsLabel ? ' The label does not change.' : ''}`}
      </Typography>
    </Alert>
  );
};

ChangeNdcLotShortfallNotice.propTypes = {
  lot: PropTypes.shape({
    lotNumber: PropTypes.string,
    remainingQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stockQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  fillQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rxNumber: PropTypes.string,
  /** The pick is the lot the row already records, so only the claim is new. */
  keepsLabel: PropTypes.bool,
};

ChangeNdcLotShortfallNotice.defaultProps = {
  lot: null,
  fillQuantity: 0,
  rxNumber: '',
  keepsLabel: false,
};

export default ChangeNdcLotShortfallNotice;
